import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Hash, Users, Dumbbell, Calendar, MessageSquare } from "lucide-react";
import PostMedia from "@/components/PostMedia";
import PollView from "@/components/PollView";
import PostActions from "@/components/PostActions";
import UserLink from "@/components/UserLink";
import RichText from "@/lib/richText";

const CHIP = {
    team: { c: "#ff8a3d", bg: "rgba(255,69,0,0.13)", bd: "rgba(255,69,0,0.4)", icon: Users },
    trainer: { c: "#4aa3f0", bg: "rgba(33,150,243,0.13)", bd: "rgba(33,150,243,0.4)", icon: Dumbbell },
    trainer_listing: { c: "#4aa3f0", bg: "rgba(33,150,243,0.13)", bd: "rgba(33,150,243,0.4)", icon: Dumbbell },
    event: { c: "#39d353", bg: "rgba(34,197,94,0.13)", bd: "rgba(34,197,94,0.4)", icon: Calendar },
    wall: { c: "#c8a2ff", bg: "rgba(168,85,247,0.13)", bd: "rgba(168,85,247,0.4)", icon: MessageSquare },
};

function Chip({ chip }) {
    if (!chip) return null;
    const st = CHIP[chip.type] || CHIP.wall;
    const Icon = st.icon;
    const inner = (
        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.06em] px-2 py-0.5 rounded-full whitespace-nowrap" style={{ color: st.c, background: st.bg, border: `1px solid ${st.bd}` }}>
            <Icon className="h-3 w-3" /> {chip.name}
        </span>
    );
    return chip.link ? <Link to={chip.link} className="hover:opacity-80">{inner}</Link> : inner;
}

export default function Tags() {
    const { tag } = useParams();
    const [items, setItems] = useState(null);

    useEffect(() => {
        setItems(null);
        api.get(`/social/hashtag/${encodeURIComponent(tag)}?limit=50`)
            .then((r) => setItems(r.data.items || []))
            .catch(() => setItems([]));
    }, [tag]);

    return (
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-10" data-testid="tags-page">
            <div className="flex items-center gap-2 mb-2">
                <Hash className="h-7 w-7 text-[#ff4500]" />
                <h1 className="font-display text-4xl sm:text-5xl tracking-wide" data-testid="tags-title">{tag}</h1>
            </div>
            <p className="text-sm text-[#a0a0a0] mb-8">Posts tagged <span className="text-[#ff4500]">#{tag}</span> from across Travel Sports.</p>

            {items == null ? (
                <div className="text-sm text-[#a0a0a0] py-16 text-center">Loading…</div>
            ) : items.length === 0 ? (
                <div className="border border-[#2a2a2a] py-16 text-center text-sm text-[#a0a0a0]" data-testid="tags-empty">
                    No posts with <span className="text-[#ff4500]">#{tag}</span> yet — be the first to use it.
                </div>
            ) : (
                <div className="space-y-4">
                    {items.map((it) => (
                        <div key={`${it.post_type}-${it.id}`} data-testid={`tag-item-${it.id}`} className="border border-[#2a2a2a] bg-[#141414] p-4">
                            <div className="flex items-center gap-2 flex-wrap">
                                <Chip chip={it.chip} />
                                <span className="text-[10px] uppercase tracking-[0.2em] text-[#666]">
                                    <UserLink username={it.author_username} /> · {new Date(it.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                                </span>
                            </div>
                            {it.subject && <div className="font-display text-lg tracking-wide mt-1">{it.subject}</div>}
                            {it.body && <div className="text-sm text-white mt-1"><RichText text={it.body} /></div>}
                            <PostMedia images={it.images} gif={it.gif} />
                            <PollView postType={it.post_type} postId={it.id} poll={it.poll} accent="#ff4500" />
                            <PostActions
                                postType={it.post_type}
                                postId={it.id}
                                link={it.link}
                                accent="#ff4500"
                                shareText={it.subject || it.body}
                                initial={{ i_reposted: it.i_reposted, repost_count: it.repost_count, i_follow: it.i_follow, follow_count: it.follow_count }}
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
